import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NewSelect, useViewport } from "@bca/ui-library";
import Button from "../../components/Button/Button";
import {
  StyledWelcomeScreenContainer,
  StyledTitle,
  StyledText,
  StyledContent,
  StyledGroupText,
  StyledListContainer,
  StyledListElement,
  StyledListIcon,
  StyledListText,
  StyledSingleText,
  StyledNewSelect,
  StyledCountryLabel,
  StyledRequired,
} from "./HomeScreen.styled";
import { getWelcomeScreen } from "../../reducers/welcomeScreenSlice";
import {
  changeCountry,
  changeCountryName,
  resetForm,
} from "../../reducers/formSlice";
import { getLanguageData } from "../../reducers/languageOptionsSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const HomeScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const languageQuery = searchParams.get("language");
  const countryQuery = searchParams.get("country");
  const { page_content, loading } = useSelector((state) => state.welcomeScreen);
  const { country_name: countrySelected } = useSelector((state) => state.form);
  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const dispatch = useDispatch();
  const vp = useViewport();
  const isMobile = vp.lessThan.md;
  const navigate = useNavigate();

  const selectCountry = (event) => {
    const countryCode = page_content.countries
      .map((country) =>
        country.country_name === event.target.value ? country : null
      )
      .filter((country) => country !== null)[0];

    dispatch(resetForm());
    dispatch(changeCountryName(countryCode.country_code));
    dispatch(getLanguageData(countryCode.country_code, languageCode));
    dispatch(changeCountry(countryCode.country_name));
  };
  const didUpdate = React.useRef(false);

  React.useLayoutEffect(() => {
    if (languageQuery && countryQuery && !didUpdate.current) {
      dispatch(getWelcomeScreen(languageQuery, false, countryQuery));
    } else if (didUpdate.current) {
      dispatch(getWelcomeScreen(languageCode, false));
    }
  }, [
    countryQuery,
    dispatch,
    languageCode,
    languageQuery,
    searchParams,
    setSearchParams,
  ]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (didUpdate.current === false) {
      dispatch(getWelcomeScreen(languageCode));
      didUpdate.current = true;
    }
  });

  React.useEffect(() => {
    if (country_iso3 !== "" && languageCode !== "") {
      if (window.digitalData) {
        updateInitializedDigitalDataForFirstPage();
        window.digitalData.push({
          site: {
            brandCode: country_iso3,
            brandName: "bca europe",
            language: languageCode,
            platformType: "cms",
            country: languageCode,
          },
          event: {
            name: "pageview",
          },
          journey: {
            name: "new-registration",
            stepNumber: "0",
            stepName: "welcome screen",
          },
        });
      } else {
        initializeDigitalData();
        window.digitalData[0] = {
          ...window.digitalData[0],
          journey: {
            name: "new-registration",
            stepNumber: "0",
            stepName: "welcome screen",
          },
        };
        window.digitalData.push({
          site: {
            brandCode: country_iso3,
            brandName: "bca europe",
            language: languageCode,
            platformType: "cms",
            country: languageCode,
          },
          event: {
            name: "pageview",
          },
          journey: {
            name: "new-registration",
            stepNumber: "0",
            stepName: "welcome screen",
          },
        });
      }
    }
  }, [country_iso3, languageCode]);

  return (
    <StyledWelcomeScreenContainer>
      {!loading && (
        <>
          <StyledContent>
            <StyledTitle variant="h1" isMobile={isMobile}>
              {page_content?.header}
            </StyledTitle>

            <StyledGroupText isMobile={isMobile}>
              <StyledText variant="body" isMobile={isMobile}>
                {page_content?.registration_message_list?.message}
              </StyledText>
              <StyledListContainer>
                {page_content?.registration_message_list?.list_of_text_fields?.map(
                  (item) => (
                    <StyledListElement key={item.item_text}>
                      <StyledListIcon name="success" size="md" />
                      <StyledListText variant="body" isMobile={isMobile}>
                        {item.item_text}
                      </StyledListText>
                    </StyledListElement>
                  )
                )}
              </StyledListContainer>
            </StyledGroupText>

            <StyledGroupText isMobile={isMobile}>
              <StyledText variant="body" isMobile={isMobile}>
                {page_content?.documents_list?.message}
              </StyledText>
              <StyledListContainer>
                {page_content?.documents_list?.list_of_text_fields?.map(
                  (item) => (
                    <StyledListElement key={item.item_text}>
                      <StyledListIcon name="success" size="md" />
                      <StyledListText isMobile={isMobile}>
                        {item.item_text}
                      </StyledListText>
                    </StyledListElement>
                  )
                )}
              </StyledListContainer>
            </StyledGroupText>

            <StyledSingleText isMobile={isMobile}>
              {page_content?.completing_registration_message}
            </StyledSingleText>
            <StyledSingleText isMobile={isMobile}>
              {page_content?.supported_formats}
            </StyledSingleText>
            <StyledSingleText isMobile={isMobile}>
              {page_content?.approval_message}
            </StyledSingleText>
            <StyledCountryLabel isMobile={isMobile}>
              {page_content?.country_label}
              <StyledRequired>*</StyledRequired>
            </StyledCountryLabel>
            <StyledNewSelect
              data-cy="country-select"
              onChange={(event) => selectCountry(event)}
              label={page_content?.country_select_message}
              value={
                page_content?.countries
                  ?.map((country) =>
                    country.country_code === countrySelected
                      ? country.country_name
                      : null
                  )
                  ?.filter((country) => country !== null)[0]
              }
              message={page_content?.country_required_message}
            >
              {page_content?.countries?.map((country) => (
                <NewSelect.Option
                  key={country.country_code}
                  value={country.country_name}
                >
                  {country.country_name}
                </NewSelect.Option>
              ))}
            </StyledNewSelect>
          </StyledContent>

          <Button
            backButtonText={page_content?.back_button?.label}
            backLink="#"
            backButtonDisabled={page_content?.back_button?.disable}
            continueButtonText={page_content?.continue_button?.label}
            nextLink="/company-information"
            nextButtonDisabled={page_content?.continue_button?.disable}
            onBackClick={() => navigate(-1)}
            isMobile={vp.lessThan.md}
          />
        </>
      )}
    </StyledWelcomeScreenContainer>
  );
};

export default HomeScreen;
